/**
 * Replaces placeholder patterns in product category names with actual values.
 *
 * Category names like "Noxious liquid, F, (2) n.o.s. (trade name ...., contains ....) ST1, Cat. X"
 * have "trade name ...." and "contains ...." placeholders that should be substituted
 * with the product's trade name and tripartite agreement "contains" value.
 *
 * Ported from the old AngularJS `replaceTradeNameAndContainsInCategoryName` function.
 */

/**
 * Replaces "trade name ...." and "contains ...." placeholders in a category name
 * with actual product-specific values when available.
 *
 * @param categoryName - The raw category name from the database (may contain placeholders)
 * @param tradeName - The product's trade name (optional)
 * @param contains - The tripartite agreement "contains" value (optional)
 * @returns The category name with placeholders replaced, or unchanged if no values provided
 */
export function formatCategoryName(
    categoryName: string | undefined | null,
    tradeName?: string | null,
    contains?: string | null
): string {
    if (!categoryName) return '';

    let result = categoryName;

    // Dot-placeholder pattern: 2 or more dots (handles "...", "....", etc.)
    const dotsPattern = '\\.{2,}';

    // Replace placeholders with actual values when available.
    // Use case-insensitive matching since DB values may use "Contains" or "contains".
    const containsPlaceholderPattern = new RegExp(`contains\\s+${dotsPattern}`, 'gi');
    const tradeNamePlaceholderPattern = new RegExp(`trade\\s+name\\s+${dotsPattern}`, 'gi');

    let containsSubstituted = false;
    if (contains && contains.trim() !== '') {
        // Replace "contains ...." placeholder with just the value.
        // The category name may already have the label word (e.g., "(Contains contains ....)"),
        // so we only emit the value to avoid "Contains contains value" duplication.
        const before = result;
        result = result.replace(containsPlaceholderPattern, contains);
        containsSubstituted = result !== before;
    }

    if (tradeName && tradeName.trim() !== '') {
        result = result.replace(tradeNamePlaceholderPattern, tradeName);
    }

    // Clean up unfilled placeholders (e.g., when viewing a product category with no trade name/contains).
    // The old AngularJS version left these visible; this version strips them for a cleaner display.
    // Remove ", contains ...." or "contains ...., " patterns (with surrounding comma/space)
    result = result.replace(new RegExp(`,\\s*contains\\s+${dotsPattern}`, 'gi'), '');
    result = result.replace(new RegExp(`contains\\s+${dotsPattern},\\s*`, 'gi'), '');
    result = result.replace(new RegExp(`contains\\s+${dotsPattern}`, 'gi'), '');

    // Remove ", trade name ...." or "trade name ...., " patterns (with surrounding comma/space)
    result = result.replace(new RegExp(`,\\s*trade\\s+name\\s+${dotsPattern}`, 'gi'), '');
    result = result.replace(new RegExp(`trade\\s+name\\s+${dotsPattern},\\s*`, 'gi'), '');
    result = result.replace(new RegExp(`trade\\s+name\\s+${dotsPattern}`, 'gi'), '');

    // Clean up empty parentheses left behind, e.g., "()" or "(  )"
    result = result.replace(/\(\s*\)/g, '');

    // Collapse multiple spaces
    result = result.replace(/ {2,}/g, ' ').trim();

    // If a contains value was provided but the category name had no placeholder for it,
    // append it explicitly so the "Contains" info is visible in the product name.
    // Example: "Exxal 13 Alcohol" + contains "Dodecyl alcohol; Alcohols (C13+)"
    //       => "Exxal 13 Alcohol (Contains Dodecyl alcohol; and Alcohols (C13+))"
    if (contains && contains.trim() !== '' && !containsSubstituted && !result.includes(contains)) {
        result = `${result} (Contains ${contains})`;
    }

    return result;
}
